import { parseEuro, type Position } from './warenkorb';

/**
 * Preis oder Rabatt einer einzelnen Bonzeile ändern – ohne Löschen und neu
 * Erfassen. Eingaben: „3,50“ = neuer Einzelpreis, „-1,50“ = Abzug auf die
 * Zeile, „10%“ = Zeilenrabatt in Prozent. Der Nachlass wird in der Bezeichnung
 * vermerkt („(−10 %)“), damit er am Beleg sichtbar bleibt (§ 132a BAO: Art und
 * Umfang der Leistung). Alles in Cent, kaufmännisch gerundet (Regel 5).
 */
const VERMERK = /\s\((−|-)[^()]*\)$/;

function basisName(bezeichnung: string): string {
  return bezeichnung.replace(VERMERK, '');
}

function euroText(cent: number): string {
  return `${String(Math.floor(cent / 100))},${String(cent % 100).padStart(2, '0')} €`;
}

/** Ein früherer Prozent-Nachlass gilt nach Abzug oder neuem Preis nicht mehr. */
function ohneNachlass(p: Position): Position {
  const kopie = { ...p };
  delete kopie.zeilenNachlass;
  delete kopie.einzelCent; // neuer Preis gilt – nicht mehr der Stammpreis
  return kopie;
}

export function passePositionAn(p: Position, eingabe: string): Position {
  const text = eingabe.trim().replace(/\s+/g, '').replace('€', '');
  if (text === '') {
    throw new RangeError('Eingabe fehlt: Einzelpreis (3,50), Abzug (-1,50) oder Rabatt (10%)');
  }
  if (p.bruttoCent < 0 || p.menge <= 0) {
    throw new RangeError('Rabatt- und Negativzeilen lassen sich nicht anpassen');
  }
  const name = basisName(p.bezeichnung);
  if (text.endsWith('%')) {
    const prozent = Number(text.slice(0, -1).replace(',', '.'));
    if (!Number.isFinite(prozent) || prozent <= 0 || prozent > 100) {
      throw new RangeError('Rabatt in Prozent zwischen 1 und 100 angeben, z. B. 10%');
    }
    const abzug = Math.round((p.bruttoCent * prozent) / 100);
    return {
      ...ohneNachlass(p),
      bezeichnung: `${name} (−${String(prozent).replace('.', ',')} %)`,
      bruttoCent: p.bruttoCent - abzug,
      zeilenNachlass: { basisCent: p.bruttoCent / p.menge, prozent },
      preisBewusst: true,
    };
  }
  if (text.startsWith('-') || text.startsWith('−')) {
    const cent = parseEuro(text.slice(1));
    if (cent === null || cent <= 0) {
      throw new RangeError('Abzug in Euro angeben, z. B. -1,50');
    }
    if (cent > p.bruttoCent) {
      throw new RangeError('Abzug ist größer als der Zeilenbetrag');
    }
    return {
      ...ohneNachlass(p),
      bezeichnung: `${name} (−${euroText(cent)})`,
      bruttoCent: p.bruttoCent - cent,
      preisBewusst: true,
    };
  }
  const einzel = parseEuro(text);
  if (einzel === null || einzel < 0) {
    throw new RangeError('Einzelpreis in Euro angeben, z. B. 3,50');
  }
  return {
    ...ohneNachlass(p),
    bezeichnung: name,
    bruttoCent: einzel * p.menge,
    preisBewusst: true,
  };
}
